/*****************************************************
 * Packages
 ******************************************************/
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
/*****************************************************
 * Locals
 ******************************************************/
import { Footer, ActionIcons, MoreActionList, EmptyCard } from './Sites.stc';
import SiteModals from './SiteModals';
import Popover from '../../Popover';
import Button from '../../Button';
import Alert from '../../Alert';
import Card from '../../Card';
import Text from '../../Text';
/*****************************************************
 * State
 ******************************************************/
import { getSites, getIsLoading, getIsAdmin } from '../../../redux/selectors';
import * as actions from '../../../redux/actions';

const ActionList = ({ onClickItem, siteId }) => {
    const isAdmin = useSelector(getIsAdmin);
    const handleSite = useCallback(
        (actionType) => {
            onClickItem(actionType, siteId);
        },
        [onClickItem, siteId]
    );

    return (
        <>
            <MoreActionList>
                {/* <li onClick={() => handleSite('TRANSFER')}>
                    <FontAwesomeIcon
                        className="icon"
                        icon={['far', 'exchange']}
                    />{' '}
                    Transfer
                </li> */}
                {isAdmin && (
                    <li>
                        <FontAwesomeIcon
                            className="icon"
                            icon={['far', 'file-export']}
                        />{' '}
                        Export
                    </li>
                )}
                <li onClick={() => handleSite('SITE_CONFIG')}>
                    <FontAwesomeIcon className="icon" icon={['far', 'cog']} />{' '}
                    Site Settings
                </li>
                {isAdmin && (
                    <li onClick={() => handleSite('MAKE_TEMPLATE')}>
                        <FontAwesomeIcon
                            className="icon"
                            icon={['far', 'cog']}
                        />{' '}
                        Make Template
                    </li>
                )}
                <hr />
                <li
                    className="action-delete-site"
                    onClick={() => handleSite('DELETE')}
                >
                    <FontAwesomeIcon
                        className="icon"
                        icon={['far', 'trash-alt']}
                    />{' '}
                    Delete Site
                </li>
            </MoreActionList>
        </>
    );
};

function Sites(props) {
    let {
        match: {
            params: { status },
        },
    } = props;
    if (status === 'unpublished') {
        status = 'draft';
    }
    const currentStatus = status ? status.toUpperCase() : null;
    const dispatch = useDispatch();
    const sites = useSelector(getSites);
    const [modal, setModal] = useState('');
    const [modalSite, setModalSite] = useState(null);
    const [modalSiteId, setModalSiteId] = useState(null);
    const isLoading = useSelector(getIsLoading);
    const filteredSites = sites.filter((site) =>
        !currentStatus ? true : currentStatus === site.status
    );

    const handleModalClose = useCallback(() => {
        setModal('');
        setModalSite(null);
    }, []);
    const setModalVisible = useCallback((modalName, siteId) => {
        setModal(modalName);
        setModalSite(sites.find((site) => site.id === siteId));
        setModalSiteId(siteId);
    }, []);

    const duplicateSite = (siteId) => {
        dispatch(actions.duplicateSite(siteId));
    };

    useEffect(() => {
        dispatch(actions.requestSites());
    }, [dispatch]);

    return (
        <div style={{ marginTop: 40 }} className="container">
            <Helmet>
                <title>Websites</title>
            </Helmet>
            <div className="flex">
                {isLoading && !filteredSites.length && (
                    <FontAwesomeIcon icon={['fal', 'spinner']} spin />
                )}
                {(sites && sites.length) || isLoading ? null : (
                    <div className="col-lg-1/3 col-md-1/2">
                        <Card>
                            <EmptyCard>
                                <FontAwesomeIcon
                                    size="3x"
                                    icon={['fal', 'layer-plus']}
                                />
                                <h5>You don’t have any website yet</h5>
                                <Link to="/start">
                                    <Button size="2x">
                                        + Create New Website
                                    </Button>
                                </Link>
                            </EmptyCard>
                        </Card>
                    </div>
                )}
                {filteredSites &&
                    filteredSites.map((site) => (
                        <div key={site.id} className="col-md-1/3 col-sm-1/2">
                            <Card
                                cover={site.img}
                                style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    marginBottom: '30px',
                                }}
                            >
                                <h3 className="title">
                                    {site.name || 'Unnamed Project'}
                                </h3>
                                <Text
                                    type="inactive"
                                    style={{ fontSize: '14px' }}
                                >
                                    {site.domain
                                        ? getUrl(site.url)
                                        : `Site ID: ${site.id}`}
                                </Text>

                                {site.status === 'DRAFT' ? (
                                    <Text
                                        type="inactive"
                                        message="Unpublished"
                                        style={{ fontWeight: 500 }}
                                    />
                                ) : (
                                    <Text
                                        type="info"
                                        message="Published!"
                                        style={{ fontWeight: 500 }}
                                    />
                                )}

                                <Footer>
                                    <Link to={`/builder?site=${site.id}`}>
                                        <Button
                                            type="primary-alt"
                                            icon={['far', 'pencil-paintbrush']}
                                        >
                                            {' '}
                                            Edit Site
                                        </Button>
                                    </Link>

                                    <ActionIcons>
                                        <Link
                                            target="_blank"
                                            to={`/preview/site/${site.id}`}
                                        >
                                            <Button
                                                type="link"
                                                className="btn-action"
                                                icon={['fal', 'eye']}
                                            />
                                        </Link>
                                        <Button
                                            type="link"
                                            className="btn-action"
                                            icon={['fal', 'copy']}
                                            onClick={() =>
                                                duplicateSite(site.id)
                                            }
                                        />

                                        <Popover
                                            placement="topRight"
                                            content={
                                                <ActionList
                                                    siteId={site.id}
                                                    onClickItem={
                                                        setModalVisible
                                                    }
                                                />
                                            }
                                        >
                                            <Button
                                                type="link"
                                                className="btn-action"
                                                icon={['fal', 'ellipsis-h-alt']}
                                            />
                                        </Popover>
                                    </ActionIcons>
                                </Footer>
                            </Card>
                        </div>
                    ))}
            </div>
            {/* TODO: move modals into more suitable place */}
            {modal && (
                <SiteModals
                    modal={modal}
                    site={modalSite}
                    siteId={modalSiteId}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
}

function getUrl(url) {
    return (
        <>
            ({' '}
            <a
                target="_blank"
                href={`https://${url}`}
                rel="noopener noreferrer"
            >
                {url}
            </a>{' '}
            )
        </>
    );
}

export default Sites;
